import { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useSideNav } from '../context/SideNavContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import StatusBadge from '../components/StatusBadge';
import ProvisionDialog from '../components/ProvisionDialog';
import Icon from '../components/Icon';
import { Loading, ErrorState, EmptyState } from '../components/States';
import { currency, relativeAge } from '../lib/format';
import { ICONS } from '../lib/icons';

// The noun a category's resources are called, used to label the service menu
// ("Instances", "Create instance", …) the way each AWS service does.
const NOUN = {
  Compute: 'instance',
  Storage: 'bucket',
  Database: 'database',
  Networking: 'network',
  Security: 'policy',
  Analytics: 'dataset',
};
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// A live listing carries the provider's own id and is never stored, so ID is 0
// on every row — the OCID is the only stable key. Falling back to the database
// key keeps stored resources working.
const rowKey = (r) => r.externalId || `db-${r.id}`;

// An OCI availability domain reads "mSyF:AP-SINGAPORE-1-AD-1": the tenancy
// prefix is noise repeated on every row, and the part after the colon is the
// placement. The full value stays in the title attribute.
const zoneLabel = (zone) => (zone || '').split(':').pop();

// The sentinel the API takes for "every subscribed region" (services.RegionsAll).
// No vendor names a region "all", so it cannot collide with a real code.
const REGION_ALL = 'all';


export default function ServiceDetail() {
  const { providerId, serviceId } = useParams();
  const [params, setParams] = useSearchParams();
  const { setNav } = useSideNav();

  // The region lives in the URL rather than in state: an instance list is per
  // region (Compute is a regional service — one call sees one region), so which
  // region is being shown is part of what the page is, and a link to it should
  // carry it. Empty means the region the integration is configured for, and
  // REGION_ALL every region the account is subscribed to.
  const region = params.get('region') || '';
  // The compartment is in the URL for the same reason: a listing covers one
  // compartment per API call, so which part of the tree is being shown is part of
  // what the page is. Empty covers the whole stored tree.
  const compartment = params.get('compartment') || '';

  const { data, loading, error, reload } = useAsync(async () => {
    const [provider, services, regions, compartments, resources] = await Promise.all([
      api.getProvider(providerId),
      api.getServices(providerId),
      // The stored subscription snapshot — the regions this tenancy can hold a
      // resource in, and therefore the only ones worth offering. A database read;
      // refreshing it lives on the provider's Region subscriptions page.
      api.getRegions(providerId),
      // The stored compartment tree, parent before child with each row's depth.
      // Also a database read, and refreshed on the provider's Compartments page.
      //
      // Swallowed on failure rather than failing the page: this read is entitled
      // per provider (a member without a contract for it gets a 403), and it only
      // feeds a picker. Losing it costs the choice, not the instance list — which
      // the backend still scopes for itself.
      api.getCompartments(providerId).catch(() => []),
      // The envelope, because a multi-region read can half-succeed: meta says
      // which regions answered and which failed.
      api.getResourcesEnvelope({ providerId, region, compartment }),
    ]);
    const service = services.find((s) => s.id === serviceId) || null;
    const items = (resources.data || []).filter((r) => r.type === serviceId);
    return {
      provider,
      service,
      regions,
      compartments,
      items,
      covered: resources.meta?.regions || [],
      coveredCompartments: resources.meta?.compartments || 0,
      failed: resources.meta?.regionErrors || {},
    };
  }, [providerId, serviceId, region, compartment]);

  const category = data?.service?.category;
  const noun = NOUN[category] || 'resource';
  const base = `/services/${providerId}/${serviceId}`;

  // Publish the service-specific left menu while this page is mounted.
  useEffect(() => {
    if (!data) return;
    setNav({
      title: data.service?.name || serviceId,
      subtitle: data.provider?.name,
      backTo: '/services',
      items: [
        { label: `${cap(noun)}s`, to: base, icon: ICONS.list },
        { label: `Create ${noun}`, to: `${base}?action=create`, icon: ICONS.create },
        { label: 'Monitoring', to: `${base}?tab=monitoring`, icon: ICONS.monitoring },
        { label: 'Settings', to: `${base}?tab=settings`, icon: ICONS.settings },
      ],
    });
  }, [data, base, noun, providerId, serviceId, setNav]);

  useEffect(() => () => setNav(null), [setNav]);

  if (loading) return <Loading label="Loading service…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const showCreate = params.get('action') === 'create';
  const closeCreate = () => {
    const next = new URLSearchParams(params);
    next.delete('action');
    setParams(next, { replace: true });
  };

  const {
    provider,
    service,
    regions,
    compartments,
    items,
    covered,
    coveredCompartments,
    failed,
  } = data;
  const failedRegions = Object.keys(failed);
  // Only active compartments can be listed in, so those are the only ones worth
  // offering as a scope.
  const pickable = compartments.filter((c) => !c.state || c.state === 'ACTIVE');
  const selected = pickable.find((c) => c.id === compartment);

  // Changing either scope replaces the whole list, so both go in the URL and the
  // fetch re-runs. Other params (a create form left open) are preserved.
  const setScope = (key) => (next) => {
    const q = new URLSearchParams(params);
    if (next) q.set(key, next);
    else q.delete(key);
    setParams(q, { replace: true });
  };
  const selectRegion = setScope('region');
  const selectCompartment = setScope('compartment');

  // A row the provider listed live carries its own id; a row created here has a
  // database key instead. Which of the two a list holds decides what there is to
  // show: an OCID and a placement exist only for the live ones, and a live list
  // is a point-in-time read of the tenancy rather than something this console
  // owns — hence Refresh.
  const live = items.some((r) => r.externalId);
  const anyZone = items.some((r) => r.zone);
  const anyCost = items.some((r) => r.monthlyCost > 0);

  // A row links to its detail only where one exists: the read is Compute's
  // (`GET /providers/{id}/instances/{ocid}`) and is keyed by the provider's own
  // id, which a stored resource does not have.
  const detailPath = (r) =>
    serviceId === 'compute' && r.externalId
      ? `${base}/instances/${encodeURIComponent(r.externalId)}`
      : null;

  return (
    <div className="stack">
      <PageHeader
        title={service?.name || serviceId}
        subtitle={service ? `${provider?.name} · ${service.category}` : provider?.name}
        actions={
          <>
            {/* Unconditional, unlike the "live" note below: an empty list is
                exactly when a re-read is worth offering — an instance launched
                in the provider's own console shows up on the next one. */}
            <button className="btn btn--ghost" onClick={reload}>
              <Icon name={ICONS.refresh} /> Refresh
            </button>
            <button
              className="btn btn--primary"
              onClick={() => setParams({ action: 'create' })}
            >
              + Create {noun}
            </button>
          </>
        }
      />

      <Card
        title={`${cap(noun)}s (${items.length})`}
        action={
          <div className="usage-export">
            {live ? (
              <span className="field__hint">
                Live from {provider?.shortName || provider?.name || providerId}
              </span>
            ) : null}
            <select
              className="input input--sm"
              aria-label="Region"
              value={region}
              onChange={(e) => selectRegion(e.target.value)}
            >
              {/* The provider's configured region, whichever that is — the
                  console does not claim to know it, since it comes from the
                  integration's own config rather than from this database. */}
              <option value="">Default region</option>
              {/* One call per subscribed region, so it is offered rather than
                  made the default — and only when the snapshot knows of more
                  than one region to cover. */}
              {regions.length > 1 ? (
                <option value={REGION_ALL}>
                  All subscribed regions ({regions.length})
                </option>
              ) : null}
              {/* A region in the URL that the snapshot does not carry — a stale
                  snapshot, or a link from an instance elsewhere — is still what
                  the list was fetched with, so it joins the options rather than
                  leaving the select showing something the page is not. */}
              {(regions.some((r) => r.id === region) ||
              !region ||
              region === REGION_ALL
                ? regions
                : [...regions, { id: region }]
              ).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.id}
                  {r.home ? ' · home' : ''}
                </option>
              ))}
            </select>

            {/* Offered only when there is a tree to pick from: with an unsynced
                snapshot the listing covers the configured compartment and there is
                no choice to make. */}
            {pickable.length > 1 ? (
              <select
                className="input input--sm"
                aria-label="Compartment"
                value={compartment}
                onChange={(e) => selectCompartment(e.target.value)}
              >
                <option value="">All compartments ({pickable.length})</option>
                {pickable.map((c) => (
                  <option key={c.id} value={c.id}>
                    {/* Non-breaking spaces: a <select> collapses ordinary ones, so
                        the tree would flatten in the dropdown. */}
                    {'  '.repeat(c.depth)}
                    {c.name || c.id}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        }
      >
        {regions.length === 0 ? (
          <p className="field__hint">
            No regions to choose from yet — refresh the region subscriptions on{' '}
            <Link to={`/providers/${providerId}/regions`}>the provider</Link> to
            list another region.
          </p>
        ) : null}

        {pickable.length === 0 ? (
          <p className="field__hint">
            No compartment tree stored yet, so this covers only the compartment the
            integration is configured for — refresh it on{' '}
            <Link to={`/providers/${providerId}/compartments`}>the provider</Link>{' '}
            to cover the rest.
          </p>
        ) : null}

        {(region === REGION_ALL && covered.length > 0) || coveredCompartments > 1 ? (
          <p className="field__hint">
            {region === REGION_ALL && covered.length > 0
              ? `Read from ${covered.length} region${
                  covered.length === 1 ? '' : 's'
                }: ${covered.join(', ')}.`
              : ''}
            {coveredCompartments > 1
              ? ` Covered ${coveredCompartments} compartments${
                  selected ? ` under ${selected.name}` : ''
                } — one API call each, per region.`
              : ''}
          </p>
        ) : null}

        {/* A region that could not be read is not a region with nothing in it,
            and the difference is invisible in the list itself. */}
        {failedRegions.length > 0 ? (
          <div className="form-error">
            Could not read{' '}
            {failedRegions.map((r) => r || 'the default region').join(', ')} —
            anything running there is missing from this list.
            {failedRegions.length === 1 ? ` ${failed[failedRegions[0]]}` : ''}
          </div>
        ) : null}

        {items.length === 0 ? (
          <EmptyState
            title={`No ${noun}s`}
            // "None yet" would be a guess: the same empty list means the regions
            // read hold none, the compartment the integration is pointed at holds
            // none, or nothing has been created here. Naming where it looked at
            // least narrows it.
            detail={
              // Where it looked, in both dimensions: a compartment scope narrows a
              // list as invisibly as a region does, and "no instances" means
              // something different under each.
              [
                `Nothing is running in ${service?.name || 'this service'}`,
                selected
                  ? `in ${selected.name} and the compartments under it`
                  : coveredCompartments === 1
                  ? 'in the configured compartment'
                  : '',
                region === REGION_ALL
                  ? 'in any subscribed region'
                  : region
                  ? `in ${region}`
                  : "in the provider's default region",
              ]
                .filter(Boolean)
                .join(' ') + '.'
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  {/* Compute sizes are shapes; every other category calls the
                      same column its spec. */}
                  <th>{category === 'Compute' ? 'Shape' : 'Spec'}</th>
                  <th>Status</th>
                  <th>Region</th>
                  {anyZone ? <th>Availability domain</th> : null}
                  <th>Created</th>
                  {anyCost ? <th className="num">Monthly cost</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={rowKey(r)}>
                    <td>
                      <div className="cell-primary">
                        {/* Only Compute has a detail read, and only a live row
                            has the provider id that read is keyed by. */}
                        {detailPath(r) ? (
                          <Link to={detailPath(r)}>{r.name || r.externalId}</Link>
                        ) : (
                          r.name || '—'
                        )}
                      </div>
                      {r.externalId ? (
                        <div className="cell-sub mono">
                          <span className="cell-id" title={r.externalId}>
                            {r.externalId}
                          </span>
                        </div>
                      ) : null}
                    </td>
                    <td className="cell-type">{r.spec || '—'}</td>
                    <td>
                      <StatusBadge status={r.status} />
                    </td>
                    <td>
                      <code className="mono">{r.region}</code>
                    </td>
                    {anyZone ? (
                      <td className="cell-sub mono" title={r.zone || ''}>
                        {zoneLabel(r.zone) || '—'}
                      </td>
                    ) : null}
                    <td
                      title={
                        relativeAge(r.createdAt)
                          ? new Date(r.createdAt).toLocaleString()
                          : ''
                      }
                    >
                      {relativeAge(r.createdAt) || '—'}
                    </td>
                    {anyCost ? (
                      <td className="num">
                        {/* A live compute listing reports no spend — the Compute
                            API has none to give — so a row without a figure says
                            so instead of reading as $0.00. */}
                        {r.monthlyCost > 0 ? currency(r.monthlyCost) : '—'}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && provider && (
        <ProvisionDialog
          providers={[provider]}
          defaultType={serviceId}
          onClose={closeCreate}
          onCreated={() => {
            closeCreate();
            reload();
          }}
        />
      )}
    </div>
  );
}
